import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../i18n';

const LANGUAGE_NAMES = {
  en: 'English',
  es: 'Spanish',
  fr: 'French'
};

function getSupportedLanguage(language) {
  const languageCode = language?.toLowerCase().split('-')[0];

  return SUPPORTED_LANGUAGES.includes(languageCode) ? languageCode : DEFAULT_LANGUAGE;
}

function SEO({ page = 'home', title, description, noindex = false, themeColor = '#8338EC' }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLang = getSupportedLanguage(i18n.resolvedLanguage || i18n.language);
  const getLocalizedUrl = (language) => {
    const pageUrl = `${t('seo.url')}${location.pathname}`;

    return language === DEFAULT_LANGUAGE ? pageUrl : `${pageUrl}?lang=${language}`;
  };
  
  const seoData = {
    title: title || t(`seo.pages.${page}.title`),
    description: description || t(`seo.pages.${page}.description`),
    siteName: t('seo.siteName'),
    url: getLocalizedUrl(currentLang),
    image: t('seo.image'),
    imageAlt: t('seo.imageAlt'),
    type: t('seo.type'),
    locale: t('seo.locale')
  };
  
  // Ensure the image URL is absolute
  const imageUrl = seoData.image.startsWith('http') 
    ? seoData.image 
    : `${t('seo.url')}${seoData.image}`;
  
  // JSON-LD structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "TapTrap",
    "alternateName": "TapTrap Party Game",
    "url": "https://www.taptrap.app",
    "description": "The ultimate party game where players put fingers on screen and let fate decide.",
    "publisher": {
      "@type": "Organization",
      "name": "TapTrap, LLC",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.taptrap.app/assets/taptrap-logo.svg"
      }
    },
    "inLanguage": SUPPORTED_LANGUAGES.map((language) => ({
      "@type": "Language",
      "name": LANGUAGE_NAMES[language] || language,
      "alternateName": language
    })),
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.taptrap.app/?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
      
      {/* Open Graph */}
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:type" content={seoData.type} />
      <meta property="og:url" content={seoData.url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={seoData.imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="675" />
      <meta property="og:site_name" content={seoData.siteName} />
      <meta property="og:locale" content={seoData.locale} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={seoData.imageAlt} />
      <meta name="twitter:site" content="@taptrapapp" />
      <meta name="twitter:creator" content="@taptrapapp" />
      
      {/* Additional Meta Tags */}
      <link rel="canonical" href={seoData.url} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="language" content={seoData.locale.split('_')[0]} />
      <meta name="author" content="TapTrap, LLC" />
      <meta name="keywords" content="taptrap, party game, finger game, drinking game, truth or dare, mobile game, friends game, social game, juego de fiesta, juego de dedos, jeu de soirée, jeu entre amis" />
      <meta name="theme-color" content={themeColor} />
      
      {/* Hreflang tags for language alternatives */}
      {SUPPORTED_LANGUAGES.map((language) => (
        <link key={language} rel="alternate" hrefLang={language} href={getLocalizedUrl(language)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={getLocalizedUrl(DEFAULT_LANGUAGE)} />
      
      {/* Mobile App Links */}
      <meta name="apple-itunes-app" content="app-id=6742859366" />
      <meta name="google-play-app" content="app-id=app.taptrap" />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

export default SEO;
